// Catalogue readiness — the numbers the pre-show readiness panel renders as
// COUNTS, not a percentage (docs/plan.md): this is the thing to check before
// leaving for a show, and a number you can compare against the manifest is
// verifiable where a progress bar is not.
//
//   Fully cached ✓ · 152 / 152 products · 78 / 78 photos
//   Downloading catalogue · 61 / 78 photos      (while running)
//
// "Products" = rows in the Dexie catalogue table vs the product endpoint's
// count. "Photos" = distinct itemNos with a cover image, cached vs in-manifest
// (each photo is TWO files — an 800px cover + a 240px thumb — so files and
// photos are tracked separately; the panel shows photos, completeness uses
// files). Session freshness rides the same panel but is derived from
// session.expiresAt in the store, not here.
import { create } from 'zustand';

export interface CatalogueReadiness {
  /** Rows in the local catalogue (product text) table. */
  productsCached: number;
  /** Products the server says exist (null until the products endpoint answers once). */
  productsExpected: number | null;
  /** Distinct itemNos with their cover image cached. */
  photosCached: number;
  /** Distinct itemNos that HAVE a photo per the manifest (null until it loads). */
  photosExpected: number | null;
  /** Every cached file (thumb + cover). */
  filesCached: number;
  /** Every file the manifest lists (null until it loads). */
  filesExpected: number | null;
  /** A precache pass is in flight. */
  running: boolean;
  /** `Date.now()` of the last pass that finished. */
  lastRunAt: number | null;
  /** Human copy from the last failure, if any. */
  lastError: string | null;
}

export interface ReadinessStore extends CatalogueReadiness {
  patch: (next: Partial<CatalogueReadiness>) => void;
}

const initial: CatalogueReadiness = {
  productsCached: 0,
  productsExpected: null,
  photosCached: 0,
  photosExpected: null,
  filesCached: 0,
  filesExpected: null,
  running: false,
  lastRunAt: null,
  lastError: null
};

export const useReadiness = create<ReadinessStore>(set => ({
  ...initial,
  patch: next => set(next)
}));

/** True when every product row and every photo file the manifest lists is on the device. */
export function isFullyCached(r: CatalogueReadiness): boolean {
  return (
    r.productsExpected !== null &&
    r.filesExpected !== null &&
    r.productsCached >= r.productsExpected &&
    r.filesCached >= r.filesExpected
  );
}
